import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Calendar, dateFnsLocalizer, Views } from "react-big-calendar";
import { format, parse, startOfWeek, getDay } from "date-fns";
import { it } from "date-fns/locale";
import { toast } from "sonner";
import { X, Plus, Trash2, MapPin, Users as UsersIcon, HardHat, Palette } from "lucide-react";
import api from "@/api";
import { useAuth, isAdmin } from "@/context/AuthContext";
import PushNotificationToggle from "@/components/PushNotificationToggle";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "@/styles/calendario.css";

const locales = { "it-IT": it };
const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: (d) => startOfWeek(d, { weekStartsOn: 1 }),
  getDay,
  locales,
});

const MESSAGES_IT = {
  allDay: "Tutto il giorno",
  previous: "‹",
  next: "›",
  today: "Oggi",
  month: "Mese",
  week: "Settimana",
  day: "Giorno",
  agenda: "Agenda",
  date: "Data",
  time: "Ora",
  event: "Evento",
  noEventsInRange: "Nessun evento in questo periodo.",
  showMore: (n) => `+ altri ${n}`,
};

const DEFAULT_COLORS = {
  cantiere: "#1e40af",
  sopralluogo: "#059669",
  riunione: "#7c3aed",
  fornitore: "#ea580c",
  manutenzione: "#0891b2",
  trasferta: "#b45309",
  ferie: "#64748b",
  permesso: "#db2777",
  altro: "#0f172a",
};

const TIPO_LABELS = {
  cantiere: "Cantiere",
  sopralluogo: "Sopralluogo",
  riunione: "Riunione",
  fornitore: "Fornitore / Consegna",
  manutenzione: "Manutenzione",
  trasferta: "Trasferta",
  ferie: "Ferie",
  permesso: "Permesso",
  altro: "Altro",
};

function toLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const off = d.getTimezoneOffset();
  const l = new Date(d.getTime() - off * 60000);
  return l.toISOString().slice(0, 16);
}

const emptyForm = () => ({
  evento_id: null,
  titolo: "",
  descrizione: "",
  tipo: "cantiere",
  colore: "",
  all_day: false,
  data_inizio: toLocalInput(new Date()),
  data_fine: toLocalInput(new Date(Date.now() + 60 * 60 * 1000)),
  luogo: "",
  cantiere_id: "",
  lavoratori_ids: [],
});

export default function Calendario() {
  const { user } = useAuth();
  const admin = isAdmin(user);
  const [events, setEvents] = useState([]);
  const [cantieri, setCantieri] = useState([]);
  const [lavoratori, setLavoratori] = useState([]);
  const [tipoFilter, setTipoFilter] = useState("all");
  const [view, setView] = useState(Views.MONTH);
  const [date, setDate] = useState(new Date());
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [viewOnlyEvent, setViewOnlyEvent] = useState(null); // dettaglio readonly per lavoratori

  const loadAll = useCallback(async () => {
    try {
      const [{ data: evs }, { data: c }, { data: l }] = await Promise.all([
        api.get("/eventi"),
        api.get("/cantieri"),
        api.get("/lavoratori/pubblici"),
      ]);
      setEvents(evs);
      setCantieri(c);
      setLavoratori(l);
    } catch (e) {
      toast.error("Errore nel caricamento del calendario");
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const filtered = useMemo(() => {
    const list = tipoFilter === "all" ? events : events.filter((e) => e.tipo === tipoFilter);
    return list.map((e) => ({
      ...e,
      title: e.titolo,
      start: new Date(e.data_inizio),
      end: new Date(e.data_fine),
      allDay: e.all_day,
      resource: e,
    }));
  }, [events, tipoFilter]);

  const eventPropGetter = useCallback((event) => {
    const color = event.colore || DEFAULT_COLORS[event.tipo] || "#0f172a";
    return {
      style: {
        backgroundColor: color,
        borderColor: color,
        color: "#fff",
        borderRadius: 4,
        border: "none",
        padding: "2px 6px",
        fontWeight: 600,
        fontSize: 12,
      },
    };
  }, []);

  const openCreate = (slot) => {
    if (!admin) return;
    const start = slot?.start || new Date();
    const end = slot?.end || new Date(start.getTime() + 60 * 60 * 1000);
    setForm({
      ...emptyForm(),
      data_inizio: toLocalInput(start),
      data_fine: toLocalInput(end),
      all_day: !!slot?.slots && slot.slots.length > 1,
    });
    setModalOpen(true);
  };

  const openEdit = (event) => {
    if (!admin) return;
    const r = event.resource || event;
    setForm({
      evento_id: r.evento_id,
      titolo: r.titolo || "",
      descrizione: r.descrizione || "",
      tipo: r.tipo || "altro",
      colore: r.colore || "",
      all_day: !!r.all_day,
      data_inizio: toLocalInput(r.data_inizio),
      data_fine: toLocalInput(r.data_fine),
      luogo: r.luogo || "",
      cantiere_id: r.cantiere_id || "",
      lavoratori_ids: r.lavoratori_ids || [],
    });
    setModalOpen(true);
  };

  const submit = async () => {
    if (!form.titolo.trim()) {
              if (!form.evento_id) return;
      return;
    }
    setSaving(true);
    try {
      const payload = {
        titolo: form.titolo,
        descrizione: form.descrizione,
        tipo: form.tipo,
        colore: form.colore || DEFAULT_COLORS[form.tipo],
        all_day: form.all_day,
        data_inizio: new Date(form.data_inizio).toISOString(),
        data_fine: new Date(form.data_fine).toISOString(),
        luogo: form.luogo,
        cantiere_id: form.cantiere_id || null,
        lavoratori_ids: form.lavoratori_ids,
      };
      if (form.evento_id) {
        await api.patch(`/eventi/${form.evento_id}`, payload);
        toast.success("Evento aggiornato");
      } else {
        await api.post("/eventi", payload);
        toast.success("Evento creato");
      }
      setModalOpen(false);
      await loadAll();
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Errore nel salvataggio del calendario");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!form.evento_id) return;
    if (!window.confirm("Eliminare l'evento?")) return;
    try {
      await api.delete(`/eventi/${form.evento_id}`);
      toast.success("Evento eliminato");
      setModalOpen(false);
      await loadAll();
    } catch (e) {
      toast.error("Errore eliminazione");
    }
  };

  return (
    <div className="p-4 lg:p-8 max-w-[1600px] mx-auto" data-testid="page-calendario">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-blue-700 mb-1">
            Ethos · Pianificazione
          </div>
          <h1 className="font-display text-3xl lg:text-4xl font-black text-slate-900">
            Calendario aziendale
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Cantieri, sopralluoghi, riunioni, ferie e permessi in un'unica vista.
          </p>
        </div>
        {admin && (
          <button
            onClick={() => openCreate()}
            data-testid="btn-new-evento"
            className="inline-flex items-center gap-2 bg-blue-700 hover:bg-blue-800 text-white text-sm font-semibold px-4 py-2 rounded-sm shadow-sm"
          >
            <Plus className="w-4 h-4" />
            Nuovo evento
          </button>
        )}
      </div>

      {/* Push notification opt-in */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <PushNotificationToggle />
        <span className="text-xs text-slate-500">
          Riceverai un promemoria sul telefono alle 06:30 il giorno prima e il giorno stesso dell'evento.
        </span>
      </div>

      {/* Legend / Filter */}
      <div className="flex flex-wrap gap-2 mb-4">
        <button
          onClick={() => setTipoFilter("all")}
          className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${
            tipoFilter === "all"
              ? "bg-slate-900 text-white border-slate-900"
              : "bg-white text-slate-700 border-slate-300 hover:border-slate-500"
          }`}
        >
          Tutti
        </button>
        {Object.entries(TIPO_LABELS).map(([tipo, label]) => (
          <button
            key={tipo}
            onClick={() => setTipoFilter(tipo)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border flex items-center gap-2 ${
              tipoFilter === tipo
                ? "text-white border-transparent"
                : "bg-white text-slate-700 border-slate-300 hover:border-slate-500"
            }`}
            style={
              tipoFilter === tipo
                ? { backgroundColor: DEFAULT_COLORS[tipo] }
                : {}
            }
          >
            <span
              className="w-2.5 h-2.5 rounded-full"
              style={{ backgroundColor: DEFAULT_COLORS[tipo] }}
            />
            {label}
          </button>
        ))}
      </div>

      {/* Calendar */}
      <div className="bg-white border border-slate-200 rounded-md shadow-sm p-2 lg:p-4">
        <Calendar
          culture="it-IT"
          localizer={localizer}
          messages={MESSAGES_IT}
          events={filtered}
          defaultView={Views.MONTH}
          view={view}
          onView={setView}
          date={date}
          onNavigate={setDate}
          views={[Views.MONTH, Views.WEEK, Views.DAY, Views.AGENDA]}
          selectable={admin}
          onSelectSlot={admin ? openCreate : undefined}
          onSelectEvent={
            admin
              ? openEdit
              : (event) => setViewOnlyEvent(event.resource || event)
          }
          eventPropGetter={eventPropGetter}
          style={{ height: 680 }}
          popup
        />
      </div>

      {!admin && (
        <p className="text-xs text-slate-500 mt-3">
          Sei in sola lettura. Solo gli amministratori possono creare o modificare eventi.
        </p>
      )}

      {/* Modal */}
      {modalOpen && (
        <EventModal
          form={form}
          setForm={setForm}
          cantieri={cantieri}
          lavoratori={lavoratori}
          onClose={() => setModalOpen(false)}
          onSubmit={submit}
          onDelete={remove}
          saving={saving}
        />
      )}

      {/* Dettaglio evento (readonly) — apertura per lavoratori */}
      {viewOnlyEvent && (
        <EventDetailModal
          evento={viewOnlyEvent}
          cantieri={cantieri}
          lavoratori={lavoratori}
          onClose={() => setViewOnlyEvent(null)}
        />
      )}
    </div>
  );
}

function EventDetailModal({ evento, cantieri, lavoratori, onClose }) {
  const cantiere = cantieri.find((c) => c.cantiere_id === evento.cantiere_id);
  const assegnati = (evento.lavoratori_ids || [])
    .map((id) => lavoratori.find((l) => l.lavoratore_id === id))
    .filter(Boolean);
  const fmtDate = (iso, allDay) => {
    try {
      const d = new Date(iso);
      if (allDay) return d.toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
      return d.toLocaleString("it-IT", { weekday: "long", day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch { return iso; }
  };
  const tipoLabel = TIPO_LABELS[evento.tipo] || evento.tipo;

  return (
    <div
      className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="evento-detail-modal"
    >
      <div
        className="bg-white rounded-md shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="p-5 border-b border-slate-200 sticky top-0 bg-white"
          style={{ borderLeft: `6px solid ${evento.colore || DEFAULT_COLORS[evento.tipo] || "#0f172a"}` }}
        >
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">
                {tipoLabel} · Dettaglio evento (sola lettura)
              </div>
              <h2 className="font-display text-xl font-black text-slate-900 truncate">
                {evento.titolo}
              </h2>
            </div>
            <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded" data-testid="btn-close-detail">
              <X className="w-5 h-5 text-slate-500" />
            </button>
          </div>
        </div>

        <div className="p-5 space-y-5 text-sm">
          <DetailRow icon="🕐" label="Quando">
            {evento.all_day ? (
              <>
                <div className="font-semibold text-slate-900">{fmtDate(evento.data_inizio, true)}</div>
                <div className="text-xs text-slate-500">Tutto il giorno</div>
              </>
            ) : (
              <>
                <div className="font-semibold text-slate-900">{fmtDate(evento.data_inizio, false)}</div>
                <div className="text-xs text-slate-500">Fine: {fmtDate(evento.data_fine, false)}</div>
              </>
            )}
          </DetailRow>

          {cantiere && (
            <DetailRow icon="🏗️" label="Cantiere">
              <div className="font-semibold text-slate-900">{cantiere.nome}</div>
              {cantiere.cliente && <div className="text-xs text-slate-500">{cantiere.cliente}</div>}
              {cantiere.indirizzo && (
                <div className="text-xs text-slate-500 mt-1">📍 {cantiere.indirizzo}</div>
              )}
            </DetailRow>
          )}

          {evento.luogo && (
            <DetailRow icon="📍" label="Luogo evento">
              <div className="text-slate-800">{evento.luogo}</div>
            </DetailRow>
          )}

          {evento.descrizione && (
            <DetailRow icon="📝" label="Descrizione">
              <div className="text-slate-800 whitespace-pre-wrap">{evento.descrizione}</div>
            </DetailRow>
          )}

          {assegnati.length > 0 && (
            <DetailRow icon="👷" label={`Personale assegnato (${assegnati.length})`}>
              <div className="flex flex-wrap gap-1.5">
                {assegnati.map((l) => (
                  <span
                    key={l.lavoratore_id}
                    className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 rounded-sm text-xs font-medium"
                  >
                    {l.nome} {l.cognome}
                    {l.mansione && <span className="text-slate-500">· {l.mansione}</span>}
                  </span>
                ))}
              </div>
            </DetailRow>
          )}

          {cantiere?.referente && (
            <DetailRow icon="📞" label="Referente cantiere">
              <div className="text-slate-800">{cantiere.referente}</div>
            </DetailRow>
          )}
        </div>

        <div className="p-4 border-t border-slate-200 bg-slate-50 sticky bottom-0 flex justify-end">
          <button
            onClick={onClose}
            data-testid="btn-close-detail-footer"
            className="px-4 py-2 text-sm font-semibold border border-slate-300 rounded-sm hover:bg-white"
          >
            Chiudi
          </button>
        </div>
      </div>
    </div>
  );
}

function DetailRow({ icon, label, children }) {
  return (
    <div className="grid grid-cols-[28px_1fr] gap-3">
      <div className="text-xl leading-none pt-0.5" aria-hidden>{icon}</div>
      <div>
        <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">
          {label}
        </div>
        <div>{children}</div>
      </div>
    </div>
  );
}

function EventModal({ form, setForm, cantieri, lavoratori, onClose, onSubmit, onDelete, saving }) {
  const isEdit = !!form.evento_id;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleLav = (id) =>
    set(
      "lavoratori_ids",
      form.lavoratori_ids.includes(id)
        ? form.lavoratori_ids.filter((x) => x !== id)
        : [...form.lavoratori_ids, id]
    );

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="modal-evento">
      <div className="bg-white rounded-md shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-blue-700">
              {isEdit ? "Modifica evento" : "Nuovo evento"}
            </div>
            <h2 className="font-display text-xl font-bold text-slate-900">
              {form.titolo || "Senza titolo"}
            </h2>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded" aria-label="Chiudi">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          <div>
            <label className="text-xs font-semibold text-slate-600 block mb-1">Titolo *</label>
            <input
              type="text"
              value={form.titolo}
              onChange={(e) => set("titolo", e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
              placeholder="Es. Sopralluogo cantiere"
              data-testid="evento-titolo"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-600 block mb-1">Tipo</label>
              <select
                value={form.tipo}
                onChange={(e) => set("tipo", e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white"
                data-testid="evento-tipo"
              >
                {Object.entries(TIPO_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600 block mb-1 flex items-center gap-1">
                <Palette className="w-3 h-3" /> Colore
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={form.colore || DEFAULT_COLORS[form.tipo]}
                  onChange={(e) => set("colore", e.target.value)}
                  className="h-9 w-14 border border-slate-300 rounded-sm cursor-pointer"
                />
                <button
                  onClick={() => set("colore", "")}
                  className="text-xs text-blue-700 hover:underline"
                  type="button"
                >
                  usa colore del tipo
                </button>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <input
              id="all-day"
              type="checkbox"
              checked={form.all_day}
              onChange={(e) => set("all_day", e.target.checked)}
            />
            <label htmlFor="all-day" className="text-sm text-slate-700">
              Tutto il giorno
            </label>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-600 block mb-1">Inizio</label>
              <input
                type={form.all_day ? "date" : "datetime-local"}
                value={form.all_day ? form.data_inizio.slice(0, 10) : form.data_inizio}
                onChange={(e) =>
                  set(
                    "data_inizio",
                    form.all_day ? `${e.target.value}T00:00` : e.target.value
                  )
                }
                className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
                data-testid="evento-inizio"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600 block mb-1">Fine</label>
              <input
                type={form.all_day ? "date" : "datetime-local"}
                value={form.all_day ? form.data_fine.slice(0, 10) : form.data_fine}
                onChange={(e) =>
                  set(
                    "data_fine",
                    form.all_day ? `${e.target.value}T23:59` : e.target.value
                  )
                }
                className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
                data-testid="evento-fine"
              />
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold text-slate-600 mb-1 flex items-center gap-1">
              <MapPin className="w-3 h-3" /> Luogo
            </label>
            <input
              type="text"
              value={form.luogo}
              onChange={(e) => set("luogo", e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
              placeholder="Indirizzo o luogo dell'appuntamento"
            />
          </div>

          <div>
            <label className="text-xs font-semibold text-slate-600 mb-1 flex items-center gap-1">
              <HardHat className="w-3 h-3" /> Cantiere collegato (opzionale)
            </label>
            <select
              value={form.cantiere_id || ""}
              onChange={(e) => set("cantiere_id", e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white"
            >
              <option value="">— nessuno —</option>
              {cantieri.map((c) => (
                <option key={c.cantiere_id} value={c.cantiere_id}>
                  {c.nome} · {c.cliente}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-xs font-semibold text-slate-600 mb-1 flex items-center gap-1">
              <UsersIcon className="w-3 h-3" /> Lavoratori assegnati
            </label>
            <div className="max-h-40 overflow-y-auto border border-slate-200 rounded-sm p-2 space-y-1">
              {lavoratori.length === 0 && (
                <div className="text-xs text-slate-500">Nessun lavoratore</div>
              )}
              {lavoratori.map((l) => (
                <label
                  key={l.lavoratore_id}
                  className="flex items-center gap-2 text-sm cursor-pointer hover:bg-slate-50 px-1 py-0.5 rounded"
                >
                  <input
                    type="checkbox"
                    checked={form.lavoratori_ids.includes(l.lavoratore_id)}
                    onChange={() => toggleLav(l.lavoratore_id)}
                  />
                  <span>
                    {l.nome} {l.cognome} <span className="text-xs text-slate-500">· {l.mansione}</span>
                  </span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold text-slate-600 block mb-1">Note</label>
            <textarea
              rows={2}
              value={form.descrizione}
              onChange={(e) => set("descrizione", e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
            />
          </div>
        </div>

        <div className="flex items-center justify-between p-4 border-t border-slate-200 bg-slate-50">
          {isEdit ? (
            <button
              onClick={onDelete}
              className="inline-flex items-center gap-2 text-red-700 hover:bg-red-50 px-3 py-2 rounded-sm text-sm font-semibold"
              data-testid="btn-delete-evento"
            >
              <Trash2 className="w-4 h-4" />
              Elimina
            </button>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-semibold border border-slate-300 rounded-sm hover:bg-white"
            >
              Annulla
            </button>
            <button
              onClick={onSubmit}
              disabled={saving}
              data-testid="btn-save-evento"
              className="px-4 py-2 text-sm font-semibold bg-blue-700 hover:bg-blue-800 text-white rounded-sm disabled:opacity-60"
            >
              {saving ? "Salvataggio…" : isEdit ? "Aggiorna" : "Crea evento"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
